/*
 * Ship-by line for blocks/product_ship-by.liquid: "Order within 13 hours 6 minutes to ship Monday".
 * Mirrors the competitor's own storefront rules (their OrderByDateDisplay code, read 27/09):
 *  - the countdown always runs to the NEXT daily cut-off in the shop time zone (today if not passed,
 *    else tomorrow), even on a non-shipping day, so it never shows more than 24 hours;
 *  - "hour/hours", "minute/minutes" by count and a zero part is dropped ("12 hours", "5 minutes");
 *  - the ship day is the first shipping day on or after that cut-off date, said as "today",
 *    "tomorrow" or the weekday name.
 * Dates come from Intl.DateTimeFormat in an IANA zone, never the visitor's clock. The pure
 * functions are exported for node so the day math is unit-tested without a browser.
 */
(function (root) {
  'use strict';
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DEFAULT_TZ = 'America/Los_Angeles';
  var fmtCache = {};

  function formatter(tz) {
    if (!fmtCache[tz]) {
      // Throws RangeError for an unknown zone; readConfig() falls back before we get here.
      fmtCache[tz] = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    }
    return fmtCache[tz];
  }

  // Wall-clock fields of an instant in a zone.
  function wallParts(ms, tz) {
    var out = {};
    formatter(tz).formatToParts(new Date(ms)).forEach(function (p) {
      if (p.type !== 'literal') out[p.type] = Number(p.value);
    });
    // Some engines still print midnight as "24" even with h23.
    if (out.hour === 24) out.hour = 0;
    return { y: out.year, m: out.month, d: out.day, h: out.hour, mi: out.minute, s: out.second };
  }

  function offsetMs(ms, tz) {
    var p = wallParts(ms, tz);
    return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000;
  }

  // Instant at which the zone's wall clock reads date + hour:00. Two passes settle the offset
  // across a DST switch (US zones switch at 02:00, far from any sane cut-off hour).
  function zonedToUtc(date, hour, tz) {
    var guess = Date.UTC(date.y, date.m - 1, date.d, hour, 0, 0);
    var first = guess - offsetMs(guess, tz);
    return guess - offsetMs(first, tz);
  }

  function addDays(date, n) {
    var t = new Date(Date.UTC(date.y, date.m - 1, date.d + n));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function isoDate(date) { return date.y + '-' + pad(date.m) + '-' + pad(date.d); }
  function weekday(date) { return new Date(Date.UTC(date.y, date.m - 1, date.d)).getUTCDay(); }

  // "2026-12-25, 2026-01-01" (commas, spaces or new lines) -> {'2026-12-25': true, ...}
  function parseHolidays(text) {
    var out = {};
    (String(text || '').match(/\d{4}-\d{2}-\d{2}/g) || []).forEach(function (d) { out[d] = true; });
    return out;
  }

  // offDays = weekday numbers with no shipping (0 = Sunday): [0, 6] Mon-Fri, [0] Mon-Sat (the
  // competitor ships Saturday mornings: "ship today" Sat before noon, "tomorrow" Fri after), [] daily.
  function isShipDay(date, cfg) {
    if (cfg.offDays.indexOf(weekday(date)) >= 0) return false;
    return !cfg.holidays[isoDate(date)];
  }

  function unit(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

  function timeText(hours, minutes) {
    var parts = [];
    if (hours > 0) parts.push(unit(hours, 'hour', 'hours'));
    if (minutes > 0) parts.push(unit(minutes, 'minute', 'minutes'));
    return parts.join(' ');
  }

  // cfg = {tz, cutoff (0-23), offDays: [0, 6], holidays: {iso: true}, relative}
  function compute(nowMs, cfg) {
    var p = wallParts(nowMs, cfg.tz);
    var today = { y: p.y, m: p.m, d: p.d };
    var cutDate = today;
    var deadline = zonedToUtc(today, cfg.cutoff, cfg.tz);
    if (nowMs >= deadline) {
      cutDate = addDays(today, 1);
      deadline = zonedToUtc(cutDate, cfg.cutoff, cfg.tz);
    }
    var ship = cutDate;
    // 60 days is far beyond any real holiday run; it only stops a bad setting from looping forever.
    for (var i = 0; i < 60 && !isShipDay(ship, cfg); i++) ship = addDays(ship, 1);
    // Real elapsed minutes (not wall-clock), floored like the competitor; the last partial
    // minute still reads "1 minute" instead of an empty sentence.
    var mins = Math.max(1, Math.floor((deadline - nowMs) / 60000));
    var hours = Math.floor(mins / 60);
    var minutes = mins % 60;
    var ahead = Math.round((Date.UTC(ship.y, ship.m - 1, ship.d) - Date.UTC(today.y, today.m - 1, today.d)) / 864e5);
    var name = DAYS[weekday(ship)];
    var word = name;
    if (cfg.relative && ahead === 0) word = 'today';
    if (cfg.relative && ahead === 1) word = 'tomorrow';
    return {
      hours: hours, minutes: minutes, time: timeText(hours, minutes),
      word: word, day: word.charAt(0).toUpperCase() + word.slice(1),
      weekday: name, date: isoDate(ship), deadline: deadline
    };
  }

  // Placeholders: [time] = "13 hours 6 minutes", [hours], [minutes], [day] = "today"/"Monday".
  function fill(template, r) {
    return String(template || 'Order within [time] to ship [day]')
      .replace(/\[time\]/g, r.time).replace(/\[hours\]/g, String(r.hours))
      .replace(/\[minutes\]/g, String(r.minutes)).replace(/\[day\]/g, r.word);
  }

  function validZone(tz) {
    try { formatter(tz); return true; } catch (e) { return false; }
  }

  function readConfig(ds) {
    var tz = String(ds.tz || '').trim() || DEFAULT_TZ;
    if (!validZone(tz)) {
      if (root.console) console.warn('[seatfit-ship-by] unknown time zone "' + tz + '", using ' + DEFAULT_TZ);
      tz = DEFAULT_TZ;
    }
    var cutoff = parseInt(ds.cutoff, 10);
    return {
      tz: tz,
      cutoff: cutoff >= 0 && cutoff <= 23 ? cutoff : 12,
      offDays: (String(ds.offDays == null ? '0,6' : ds.offDays).match(/[0-6]/g) || []).map(Number),
      holidays: parseHolidays(ds.holidays),
      relative: ds.dayWords !== 'weekday'
    };
  }

  var api = { compute: compute, fill: fill, readConfig: readConfig, parseHolidays: parseHolidays, zonedToUtc: zonedToUtc };
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.SeatFitShipBy = api;

  if (typeof customElements === 'undefined' || customElements.get('seatfit-ship-by')) return;

  // Other scripts (the vehicle-steps buy button "ADD TO CART - SHIPS {DAY}") may load before or
  // after this one, so the day is both kept on <html> (read at init) and announced on change.
  function publish(r, text) {
    var ds = document.documentElement.dataset;
    ds.seatfitShipDay = r.day;
    ds.seatfitShipDate = r.date;
    ds.seatfitShipWeekday = r.weekday;
    document.dispatchEvent(new CustomEvent('seatfit:ship-day', {
      detail: { day: r.day, date: r.date, weekday: r.weekday, word: r.word, text: text }
    }));
  }

  class SeatfitShipBy extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[data-sf-ship-by-text]') || this;
      this.tick = this.tick.bind(this);
      this.onVisible = function () { if (!document.hidden) this.tick(); }.bind(this);
      try {
        this.cfg = readConfig(this.dataset);
      } catch (e) {
        this.hidden = true; // no Intl time-zone support: say nothing rather than a wrong day
        return;
      }
      this.tick();
      this.timer = setInterval(this.tick, 30000);
      // Background tabs throttle timers; refresh as soon as the page is looked at again.
      document.addEventListener('visibilitychange', this.onVisible);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisible);
    }

    tick() {
      try {
        var r = compute(Date.now(), this.cfg);
        var text = fill(this.dataset.template, r);
        if (this.textEl.textContent !== text) this.textEl.textContent = text;
        this.hidden = false;
        var key = r.day + '|' + r.date;
        if (key !== this.lastKey) {
          this.lastKey = key;
          publish(r, text);
        }
      } catch (e) {
        this.hidden = true;
        if (root.console) console.warn('[seatfit-ship-by]', e);
      }
    }
  }
  customElements.define('seatfit-ship-by', SeatfitShipBy);
})(typeof window !== 'undefined' ? window : globalThis);
